import type { QueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import type { FilterSelection } from '@/scopes/prices/domain/filters/types'
import type {
  TransportSelectorContext,
  TransportSelectorExtraFilters,
  TransportSelectorFilters,
} from '@/scopes/prices/infrastructure/transport/types'
import { createBrowserQueryString } from '@/scopes/common/infrastructure/query-strings/browserQueryString'
import { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types/browserQueryString'
import filterQueryStringSerializer
  from '@/scopes/prices/infrastructure/filter-query-string-serializer/filterQueryStringSerializer'
import { pricesPassThroughParams } from '@/scopes/prices/types'
import { getPassThroughParams } from '@/scopes/search/search/domain/getPassThroughParams'

export enum TransportType {
  Flight = 'Flight',
  SelfDrive = 'SelfDrive',
  Bus = 'Bus',
}

const apiEndpoints: Record<TransportType, string> = {
  Flight: 'FlightSelector/GetFlightSelectorApi',
  SelfDrive: 'SelfdriveSelector/GetSelfdriveSelectorApi',
  Bus: 'BusSelector/GetBusSelectorApi',
}

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  queryStringSerializer: QueryStringSerializer<
    TransportSelectorContext & FilterSelection & TransportSelectorExtraFilters
  >
  browserQuery: BrowserQueryString
}

export interface TransportSelectorUrlBuilder {
  buildUrl: (
    criteria: TransportSelectorContext & { filterSelection: FilterSelection } & TransportSelectorFilters,
    transportType: TransportType
  ) => string
}

export function createTransportSelectorUrlBuilder(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    queryStringSerializer = filterQueryStringSerializer,
    browserQuery = createBrowserQueryString(),
  } = dependencies as Dependencies

  function buildUrl(
    {
      accoId,
      contextItemId,
      bookingGateId,
      filterSelection,
      selectedPriceId,
      roomIds,
      roomContractIds,
    }: TransportSelectorContext & { filterSelection: FilterSelection } & TransportSelectorFilters,
    transportType: TransportType,
  ) {
    const roomSelectorContext = {
      accoId,
      bookingGateId,
      contextItemId,
    }

    const duration = selectedPriceId?.split('|')[0]
    const departureDate = selectedPriceId?.split('|')[1]

    const queryParams = new URLSearchParams(browserQuery.get()!)
    const passThroughParams = getPassThroughParams(queryParams, pricesPassThroughParams)

    const serializedCriteria = queryStringSerializer.serialize({
      ...roomSelectorContext,
      ...filterSelection,
      ...passThroughParams,
      dateFrom: departureDate,
      dateTo: departureDate,
      duration,
      room: roomIds,
      roomContractId: roomContractIds,
    })

    const path = apiEndpoints[transportType]

    return `${env.NEXT_PUBLIC_API_BASE_URL}/${path}?${serializedCriteria}`
  }
  return { buildUrl }
}
